// A ladda plugin, sibling to `observable()` from ladda-observable, that attaches
// `createResource(...)` to every READ operation for Suspense-based data fetching:
//
//     const resource = api.users.getUser.createResource(userId);
//     // fetching starts immediately; read the value with useData(), which
//     // suspends until it resolves and re-renders in place on cache changes
//     // afterwards (never re-suspending a component that already has data).
//
// The relationship/relevance-detection logic below is ported from
// ladda-observable (github.com/ladda-js/ladda-observable, src/helper.js +
// src/index.js) since the published package doesn't export it for reuse -
// it decides whether a given cache change is relevant to a given entity/fn,
// the same way `observable()` does for `createObservable`.
//
// No separate promise cache is kept here for in-flight requests - it's not
// needed. Each resource instance calls fn(...args) exactly once while it has
// no data, and serves that same stored promise on every read until it
// settles, so use() always sees a stable promise across a component's
// re-renders. Two independent resource instances for the same (entity, fn,
// args) may each get a *different* promise object from ladda's dedup plugin
// (it hands the originating caller a `.then()`-derived promise and later
// concurrent callers the raw one) - but dedup still guarantees only one
// underlying fetch happens, which is the actual guarantee this relies on.
// When ladda-cache exposes a synchronous `fn.getCached(...args)` (added for
// this purpose - see ladda/src/plugins/cache/operations/read.js), it's used
// opportunistically to skip the promise path entirely when data is already
// warm, and to make a freshly (re)created resource resync from ladda's
// cache instead of forgetting it ever had data.

type Operation = 'CREATE' | 'READ' | 'UPDATE' | 'DELETE' | 'COMMAND' | 'NO_OPERATION';

interface LaddaChange {
    operation: Operation;
    entity: string;
    apiFn: string;
    values: unknown[] | null;
    args: unknown[];
}

type CachePeek = { isCached: true; value: any } | { isCached: false };

interface LaddaApiFn {
    (...args: any[]): Promise<any>;
    operation: Operation;
    fnName: string;
    invalidates?: string[];
    getCached?: (...args: any[]) => CachePeek;
    createResource?: (...args: any[]) => Resource<any>;
}

interface LaddaEntity {
    name: string;
    api: Record<string, LaddaApiFn>;
}

interface LaddaEntityConfig {
    name: string;
    viewOf?: string;
    invalidates?: string[];
    api: Record<string, LaddaApiFn>;
}

interface Relationship {
    views: string[];
    parents: string[];
    invalidatedBy: string[];
}

type Relationships = Record<string, Relationship>;

// --- relationship analysis (ported, see file header) ---

const getOrCreateContainer = (type: string, relationships: Relationships): Relationship => {
    if (!relationships[type]) {
        relationships[type] = { views: [], parents: [], invalidatedBy: [] };
    }
    return relationships[type];
};

const getParentViews = (configs: Record<string, LaddaEntityConfig>, type: string, res: string[] = []): string[] => {
    const config = configs[type];
    if (config.viewOf) {
        return getParentViews(configs, config.viewOf, [...res, config.viewOf]);
    }
    return res;
};

const analyzeViews = (configs: Record<string, LaddaEntityConfig>, type: string, rels: Relationships): Relationships => {
    const container = getOrCreateContainer(type, rels);
    const parents = getParentViews(configs, type);
    container.parents = [...container.parents, ...parents];
    parents.forEach(parent => {
        getOrCreateContainer(parent, rels).views.push(type);
    });
    return rels;
};

const analyzeInvalidations = (configs: Record<string, LaddaEntityConfig>, rels: Relationships): Relationships => {
    Object.keys(rels).forEach(type => {
        (configs[type].invalidates || []).forEach(invalidatedType => {
            rels[invalidatedType].invalidatedBy.push(type);
        });
    });
    return rels;
};

const analyzeEntityRelationships = (configs: Record<string, LaddaEntityConfig>): Relationships => {
    const relationships = Object.keys(configs).reduce<Relationships>((rels, type) => {
        return analyzeViews(configs, type, rels);
    }, {});
    return analyzeInvalidations(configs, relationships);
};

// --- relevance detection (ported, see file header) ---

const isNoOperation = (change: LaddaChange) => change.operation === 'NO_OPERATION';
const isChangeOfSameEntity = (entity: LaddaEntity, change: LaddaChange) =>
    !isNoOperation(change) && entity.name === change.entity;
const isChangeOfView = (rel: Relationship, change: LaddaChange) =>
    !isNoOperation(change) && rel.views.indexOf(change.entity) !== -1;
const isChangeOfParent = (rel: Relationship, change: LaddaChange) =>
    !isNoOperation(change) && rel.parents.indexOf(change.entity) !== -1;
const isInvalidatedByChange = (rel: Relationship, change: LaddaChange) =>
    rel.invalidatedBy.indexOf(change.entity) !== -1;
const isInvalidatedByFunction = (entity: LaddaEntity, fn: LaddaApiFn, change: LaddaChange) => {
    if (change.entity !== entity.name) {
        return false;
    }
    const invalidations = entity.api[change.apiFn].invalidates || [];
    return invalidations.length > 0 && invalidations.indexOf(fn.fnName) !== -1;
};

const isRelevantChange = (
    relationships: Relationships,
    entity: LaddaEntity,
    fn: LaddaApiFn,
    change: LaddaChange
): boolean => {
    const rel = relationships[entity.name];
    return (
        isChangeOfSameEntity(entity, change) ||
        isChangeOfView(rel, change) ||
        isChangeOfParent(rel, change) ||
        isInvalidatedByChange(rel, change) ||
        isInvalidatedByFunction(entity, fn, change)
    );
};

// --- resource ---

export interface ResourceSnapshot<T> {
    hasData: boolean;
    data?: T;
    promise?: Promise<T>;
}

export interface Resource<T> {
    subscribe: (onStoreChange: () => void) => () => void;
    getSnapshot: () => ResourceSnapshot<T>;
}

const NOT_CACHED: CachePeek = { isCached: false };

interface PluginState {
    changeListeners: Array<(change: LaddaChange) => void>;
}

const toArgsKey = (args: any[]) => JSON.stringify(args);

// How long a failed fetch's rejected promise is kept around and handed back
// to a fresh createResource() call for the same args, instead of firing a
// new request. This isn't about deduping concurrent calls (dedupPlugin
// already does that) - it's specifically to survive React's own error
// recovery: on an uncaught render error, React retries the failing subtree
// once synchronously "from scratch" to rule out a concurrent-rendering
// fluke, and "from scratch" discards useMemo state too - so a fresh
// createResource() call happens, starting a fresh fetch with a fresh
// promise. If that also fails, React sees what looks like a *new* error
// (different object) each time and keeps retrying, entirely internally, far
// faster than any real network round-trip - tens of thousands of times
// within milliseconds in practice. Handing back the exact same (already
// rejected) promise object gives React a stable reference to recognize as
// "the same failure happening again", which stops the retry. The cooldown
// is what lets a later, genuine retry (new mount after the backend
// recovers) try again instead of being stuck forever.
const FAILURE_COOLDOWN_MS = 3000;

const createResourceFactory =
    (
        state: PluginState,
        relationships: Relationships,
        entity: LaddaEntity,
        fn: LaddaApiFn,
        // Scoped per (entity, fn) - shared by every createResource(...args) call
        // for this one API function, the same way dedupPlugin scopes its own
        // in-flight cache. Must NOT be shared across different API functions:
        // args from unrelated calls could otherwise collide on the same key.
        recentFailures: Record<string, Promise<any>>
    ) =>
    (...args: any[]): Resource<any> => {
        let subscribers: Array<() => void> = [];
        let snapshot: ResourceSnapshot<any> = { hasData: false };

        const setSnapshot = (next: ResourceSnapshot<any>) => {
            snapshot = next;
            subscribers.forEach(subscriber => subscriber());
        };

        const load = () => {
            const cached = fn.getCached ? fn.getCached(...args) : NOT_CACHED;
            if (cached.isCached) {
                setSnapshot({ hasData: true, data: cached.value });
                return;
            }

            const argsKey = toArgsKey(args);
            const hasRecentFailure = !snapshot.hasData && argsKey in recentFailures;
            const promise = hasRecentFailure ? recentFailures[argsKey] : fn(...args);

            // Only replace the snapshot's promise while we don't have data yet -
            // once we do, this load() is a background revalidation and the old
            // data keeps being served (stale-while-revalidate) until it resolves.
            if (!snapshot.hasData) {
                snapshot = { hasData: false, promise };
            }

            if (hasRecentFailure) {
                return;
            }

            promise.then(
                data => {
                    delete recentFailures[argsKey];
                    setSnapshot({ hasData: true, data });
                },
                () => {
                    // A background revalidation failure is swallowed: we keep
                    // serving the last good snapshot rather than blowing away
                    // already-rendered content. A *first-load* failure isn't
                    // handled here - it's surfaced by use() throwing directly
                    // from the (still referenced, now-rejected) snapshot.promise.
                    recentFailures[argsKey] = promise;
                    setTimeout(() => {
                        if (recentFailures[argsKey] === promise) {
                            delete recentFailures[argsKey];
                        }
                    }, FAILURE_COOLDOWN_MS);
                }
            );
        };

        const changeListener = (change: LaddaChange) => {
            if (isRelevantChange(relationships, entity, fn, change)) {
                load();
            }
        };

        const subscribe = (onStoreChange: () => void) => {
            if (subscribers.length === 0) {
                state.changeListeners.push(changeListener);
                load();
            }
            subscribers.push(onStoreChange);
            return () => {
                subscribers = subscribers.filter(subscriber => subscriber !== onStoreChange);
                if (subscribers.length === 0) {
                    state.changeListeners = state.changeListeners.filter(l => l !== changeListener);
                }
            };
        };

        load();

        return {
            subscribe,
            getSnapshot: () => snapshot,
        };
    };

export const resource = () => {
    return ({
        addChangeListener,
        entityConfigs,
    }: {
        addChangeListener: (listener: (change: LaddaChange) => void) => void;
        entityConfigs: Record<string, LaddaEntityConfig>;
    }) => {
        const state: PluginState = { changeListeners: [] };
        const relationships = analyzeEntityRelationships(entityConfigs);

        addChangeListener((change: LaddaChange) => {
            state.changeListeners.forEach(listener => listener(change));
        });

        return ({ entity, fn }: { entity: LaddaEntity; fn: LaddaApiFn }) => {
            if (fn.operation !== 'READ') {
                return fn;
            }
            const recentFailures: Record<string, Promise<any>> = {};
            fn.createResource = createResourceFactory(state, relationships, entity, fn, recentFailures);
            return fn;
        };
    };
};
